<template>
    <div class="mt-5">
        <information-modal
            title="Reject Narrative"
            :show-modal="isShowNarrativeReviewEditor"
            @dismiss="hideNarrativeReviewEditor">
            <form @submit.prevent="reviewNarrative">
                <fieldset>
                    <div class="mb-3">
                        <label
                            for="reviewComment"
                            class="form-label"
                            >Comment</label
                        >

                        <textarea
                            type="text"
                            v-model="review.comment"
                            id="reviewComment"
                            class="form-control">
                        </textarea>
                    </div>
                </fieldset>

                <div class="d-grid gap-2 d-md-flex justify-content-md-end">
                    <button
                        type="submit"
                        class="btn btn-primary">
                        Reject
                    </button>
                </div>
            </form>
        </information-modal>

        <information-modal
            title="Review Incident"
            :show-modal="isShowIncidentReviewEditor"
            @dismiss="hideIncidentReviewEditor">
            <form @submit.prevent="reviewIncident">
                <fieldset>
                    <div class="mb-3">
                        <label
                            for="reviewStatus"
                            class="form-label"
                            >Status</label
                        >
                        <select
                            id="reviewStatus"
                            v-model="review.status"
                            class="form-select"
                            :class="{'is-invalid': !!errors.status}">
                            <option value="">Select Status</option>
                            <option value="IF">Reject Incident</option>
                            <option value="IP">Accept Incident</option>
                        </select>
                        <span
                            class="invalid-feedback"
                            role="alert"
                            v-if="errors.status">
                            {{ errors.status }}
                        </span>
                    </div>
                    <div class="mb-3">
                        <label
                            for="reviewComment"
                            class="form-label"
                            >Comment</label
                        >

                        <textarea
                            type="text"
                            v-model="review.comment"
                            id="reviewComment"
                            class="form-control">
                        </textarea>
                    </div>
                </fieldset>

                <div class="d-grid gap-2 d-md-flex justify-content-md-end">
                    <button
                        type="submit"
                        class="btn btn-primary">
                        Save
                    </button>
                </div>
            </form>
        </information-modal>

        <information-modal
            :title="uploadNarrativeTitle"
            :show-modal="isShowUploadNarrative"
            @dismiss="hideUploadNarrative">
            <form>
                <fieldset class="border-bottom mb-3">
                    <div class="mb-3 row">
                        <div class="col-md-3">
                            <label
                                for="uploadNarrative"
                                class="col-form-label"
                                >File:</label
                            >
                        </div>
                        <div class="col-md-8">
                            <input
                                type="file"
                                ref="uploadNarrative"
                                id="uploadNarrative"
                                class="form-control" />
                        </div>
                    </div>
                </fieldset>

                <div class="d-grid gap-2 d-md-flex justify-content-md-end">
                    <button
                        type="button"
                        class="btn btn-secondary"
                        data-bs-dismiss="modal">
                        Cancel
                    </button>
                    <button
                        @click.prevent="uploadDocument"
                        class="btn btn-primary"
                        data-bs-dismiss="modal">
                        Save
                    </button>
                </div>
            </form>
        </information-modal>

        <information-modal
            title="Delete"
            :show-modal="isConfirmDialog"
            :show-footer="true"
            className="confirm"
            @dismiss="hideConfirmDialog">
            Are You Sure?
            <template v-slot:footer>
                <button
                    type="button"
                    class="btn btn-secondary"
                    data-bs-dismiss="modal">
                    No
                </button>

                <button
                    type="button"
                    class="btn btn-primary"
                    @click="deleteIncident">
                    Yes
                </button>
            </template>
        </information-modal>

        <template v-if="runAudits">
            <audit-jobs
                :run-audits="runAudits"
                :create-audits="createAudits"
                :audit="audit"
                :id="id"></audit-jobs>
        </template>

        <h2>Incidents</h2>

        <b-grid
            caption="List of jobs"
            :tableOptions="auditIncidentStore.tableOptions"
            :filterableFields="auditIncidentStore.filterableFields"
            :filterOptions="auditIncidentStore.filterOptions"
            @optionChange="getData"
            :items="auditIncidentStore.items"
            :columns="columns"
            :loading="auditIncidentStore.loading">
            <template
                #beforeFilter
                v-if="canCheckIncidentsVersion">
                <button
                    type="button"
                    class="btn btn-primary"
                    @click.prevent="checkIncidentsVersion()">
                    Update Incident Versions
                </button>
            </template>

            <template v-slot:cell.incidentNumber="{item, column}">
                <a
                    target="_blank"
                    :href="`MSIVReportViewer.aspx?IncidentNumber=${item.incidentNumber}&ORI=${item.ori}`"
                    >{{ item.incidentNumber }}</a
                >
            </template>

            <template v-slot:cell.status="{item, column}">
                {{ item.statusName }}

                <i
                    class="bi bi-info-circle-fill text-info"
                    v-if="item.comment"
                    v-tooltip="item.comment"></i>

                <i
                    class="bi bi-info-circle-fill text-info"
                    v-else-if="item.narrative && item.narrative.Comment"
                    v-tooltip="item.narrative.Comment"></i>
                <template v-if="!item.loadId"> Deleted </template>
                <template v-else-if="item.actionLoadId && item.loadId != item.actionLoadId"> Updated </template>
            </template>
            <template v-slot:cell.actions="{item, column}">
                <span v-if="item.narrative == null">
                    <button
                        class="btn btn-primary btn-sm"
                        v-if="item.status == 'NR' && !runAudits"
                        @click.prevent="showUploadNarrative(item)">
                        Upload Narrative
                    </button>
                </span>
                <template v-else>
                    <a
                        class="btn btn-sm btn-secondary ms-1"
                        :href="`api/v1/audit/file/${item.narrative.FileId}`"
                        >View Narrative</a
                    >

                    <button
                        class="btn btn-primary btn-sm ms-1"
                        v-if="item.narrative.Status != 'DA' && !runAudits"
                        @click.prevent="showUploadNarrative(item)">
                        Replace Narrative
                    </button>
                </template>
                <template v-if="runAudits && item.narrative">
                    <button
                        class="btn btn-danger btn-sm ms-1"
                        v-if="item.narrative.Status == 'DU' && item.status == 'IN'"
                        @click.prevent="showNarrativeReviewEditor(item.id)">
                        Reject Narrative
                    </button>

                    <button
                        class="btn btn-primary btn-sm ms-1"
                        v-if="item.narrative.Status == 'DU' && item.status == 'IN'"
                        @click.prevent="showIncidentReviewEditor(item.id)">
                        Review Incident
                    </button>

                    <button
                        class="btn btn-primary btn-sm ms-1"
                        v-if="item.status == 'IF' || item.status == 'IP'"
                        @click.prevent="showIncidentReviewEditor(item.id)">
                        Update Review
                    </button>
                    <span v-else> </span>
                </template>
                <span v-else> </span>
                <button
                    class="btn btn-danger btn-sm ms-1"
                    v-if="runAudits"
                    @click.prevent="deleteItem(item.id)">
                    Delete Request
                </button>
            </template>
        </b-grid>
    </div>
</template>
<script lang="ts" setup>
import {BTableColumn} from "../BTable/BTable.types";

import InformationModal from "../Misc/InformationModal.vue";
import AuditJobs from "./AuditJobs.vue";
import BGrid from "../BGrid/BGrid.vue";
import {onMounted, reactive, ref} from "vue";
import {useAuditIncidentStore} from "@/stores/auditIncidentStore";
import {object} from "yup";
import {Audit, AuditReviewDocument} from "@/audit.types";
import {useFormValidation} from "@/validation";
import {debounce} from "vue-debounce";

const props = defineProps<{
    id: number;
    audit: Audit;
    runAudits: boolean;
    createAudits: boolean;
}>();

var updateData = debounce(() => getData(), "15000ms");

const isShowUploadNarrative = ref(false);
const selectedIncindentId = ref(0);
const uploadNarrativeTitle = ref("");
const isConfirmDialog = ref(false);
const canCheckIncidentsVersion = ref(false);
const auditIncidentStore = useAuditIncidentStore();

const hideUploadNarrative = () => {
    isShowUploadNarrative.value = false;
};

const showUploadNarrative = (incident) => {
    isShowUploadNarrative.value = true;
    selectedIncindentId.value = incident.id;
    uploadNarrativeTitle.value = `Upload Narrative for incident ${incident.incidentNumber}`;
    return false;
};

const columns = reactive<BTableColumn[]>([
    {
        key: "incidentNumber",
        text: "Incident Number",
        sortable: true,
        value: (item) => item["incidentNumber"],
    },
    {
        key: "incidentType",
        text: "Type",
        sortable: true,
        value: (item) => item.type,
    },
    {
        key: "incidentDate",
        text: "Incident/Arrest Date",
        sortable: true,
        value: (item) => item["date"],
    },
    {
        key: "status",
        text: "Status",
        sortable: false,
        value: (item) => item["statusName"],
    },
    {
        key: "actions",
        text: "",
        columnClass: ["text-end"],
        class: (item) => ["text-nowrap text-end"],
        sortable: false,
        value: (item) => 2,
    },
]);

const {handleSubmit, errors, resetValidation} = useFormValidation();
const uploadNarrative = ref<File | null>();
const isShowNarrativeReviewEditor = ref(false);

const showNarrativeReviewEditor = (id: number) => {
    resetValidation();
    review.value = {status: ""} as AuditReviewDocument;
    selectedIncindentId.value = id;
    isShowNarrativeReviewEditor.value = true;
};

const hideNarrativeReviewEditor = () => {
    isShowNarrativeReviewEditor.value = false;
};

const isShowIncidentReviewEditor = ref(false);

const showIncidentReviewEditor = (id: number) => {
    resetValidation();
    selectedIncindentId.value = id;
    review.value = {status: ""} as AuditReviewDocument;
    isShowIncidentReviewEditor.value = true;
};

const hideIncidentReviewEditor = () => {
    isShowIncidentReviewEditor.value = false;
};
const reviewIncident = async function () {
    if (await handleSubmit(reviewSchema, review.value)) {
        review.value.id = selectedIncindentId.value;
        await auditIncidentStore.reviewIncident(review.value);
        hideIncidentReviewEditor();
        await getData();
    }
};

const reviewSchema = object<AuditReviewDocument>({});

const review = ref({} as AuditReviewDocument);

const reviewNarrative = async function () {
    if (await handleSubmit(reviewSchema, review.value)) {
        review.value.id = selectedIncindentId.value;
        await auditIncidentStore.reviewNarrative(review.value);
        hideNarrativeReviewEditor();
        await getData();
    }
};

const checkIncidentsVersion = async () => {
    await auditIncidentStore.checkIncidentsVersion(props.id);
    await getData();
};

const hideConfirmDialog = async function () {
    isConfirmDialog.value = false;
};
const deleteIncident = async function () {
    await auditIncidentStore.deleteItem(selectedIncindentId.value);
    await getData();
    await hideConfirmDialog();
};
const deleteItem = async function (id: number) {
    selectedIncindentId.value = id;
    isConfirmDialog.value = true;
};

const getData = async () => {
    await auditIncidentStore.list(props.audit.id);
    await updateData();
};

const uploadDocument = async () => {
    await auditIncidentStore.uploadNarrative(selectedIncindentId.value, uploadNarrative);
    await getData();
    hideUploadNarrative();
    uploadNarrative.value.value = "";
};

onMounted(async () => {
    await getData();
});
</script>
